import { FC, useMemo, useCallback } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { shallow } from "zustand/shallow";
import { FilterChip } from "@/components/filters/FilterChip";
import { useFilterDraft } from "@/hooks/filters";
import { useGeographyLabel } from "@/hooks/useGeographyLabel";
import { LocationsFilter } from "@/components/filters/Locations/LocationsFilter";
import { LOCATION_TYPES } from "@/components/filters/Locations/constants";
import type { LocationsPayload } from "@/components/filters/Locations/types";
import { useIntentFiltersStorage } from "../hooks/useIntentFiltersStorage";
import Box from "@mui/material/Box";

export const LocationFilter: FC = () => {
  const { intentFilters, setIntentFilters } = useIntentFiltersStorage();

  // Extract location fields to avoid re-creating locationValue when unrelated filters change
  const { locationCountryAlpha2s, locationRegionNames, locationStateIds } =
    intentFilters;

  const locationValue = useMemo((): LocationsPayload => {
    let selectedType: LocationsPayload["selectedLocationType"] =
      LOCATION_TYPES.COUNTRY;
    if (locationRegionNames.length > 0) {
      selectedType = LOCATION_TYPES.REGION;
    } else if (locationStateIds.length > 0) {
      selectedType = LOCATION_TYPES.STATE;
    } else if (locationCountryAlpha2s.length > 0) {
      selectedType = LOCATION_TYPES.COUNTRY;
    }

    return {
      selectedLocationType: selectedType,
      countryAlpha2s: locationCountryAlpha2s,
      regionIds: locationRegionNames,
      stateIds: locationStateIds,
      excludedCountryAlpha2s: [],
    };
  }, [locationCountryAlpha2s, locationRegionNames, locationStateIds]);

  const locationDraft = useFilterDraft(
    locationValue,
    (value) => {
      setIntentFilters((prev) => ({
        ...prev,
        locationCountryAlpha2s: value.countryAlpha2s,
        locationRegionNames: value.regionIds,
        locationStateIds: value.stateIds,
      }));
    },
    {
      getEmptyValue: () => ({
        selectedLocationType: LOCATION_TYPES.COUNTRY,
        countryAlpha2s: [],
        regionIds: [],
        stateIds: [],
        excludedCountryAlpha2s: [],
      }),
      isEqual: shallow,
    }
  );

  const handleClear = useCallback(() => {
    setIntentFilters((prev) => ({
      ...prev,
      locationCountryAlpha2s: [],
      locationRegionNames: [],
      locationStateIds: [],
    }));
  }, [setIntentFilters]);

  const geographyLabel = useGeographyLabel({
    countryAlpha2s: locationCountryAlpha2s,
    regionNames: locationRegionNames,
    stateIds: locationStateIds,
  });
  const label = geographyLabel || "All";

  const count =
    locationCountryAlpha2s.length +
    locationRegionNames.length +
    locationStateIds.length;

  const getSelectedCount = useCallback(() => count, [count]);

  const popoverOptions = useMemo(
    () => ({
      onClearButtonClick: locationDraft.clearDraft,
      onCancelButtonClick: locationDraft.cancelChanges,
      onApplyButtonClick: locationDraft.applyChanges,
      disableApplyButton: !locationDraft.hasChanges,
      disableCancelButton: false,
    }),
    [locationDraft]
  );

  return (
    <FilterChip
      label="Location"
      getSelectedCount={getSelectedCount}
      onDelete={count > 0 ? handleClear : undefined}
      renderLabel={() => (
        <Stack flexDirection="row" gap="3px">
          <Typography color="secondary">Location:</Typography>
          <Typography>{label}</Typography>
        </Stack>
      )}
      paperStyles={{ width: 450 }}
      popoverOptions={popoverOptions}
    >
      <Box id="intent-location-filter" mb={1}>
        <LocationsFilter
          selectedValues={locationDraft.draft}
          onValuesChange={locationDraft.updateDraft}
          showExcluded={false}
          config={{
            collectionSelect: { enabled: false },
            countrySelect: { disabledForState: true },
          }}
        />
      </Box>
    </FilterChip>
  );
};
